/** @format */

/* eslint @typescript-eslint/prefer-nullish-coalescing: "warn" -- Legacy code: do not use as an example. */
import isPropValid from '@emotion/is-prop-valid';
import React, { FunctionComponent, ReactNode } from 'react';
import styled from 'styled-components';

import { EmotionDynamicElement } from '@toucan/client/global/application';
import { WrappedLink, WrappedLinkProps } from '@toucan/client/global/nextjs';

import {
    Link,
    LinkColor,
    LinkProps,
    LinkSize,
} from '@toucan/client/shared/components/link';

import { styles } from './link-list.styles';

// eslint-disable-next-line @mpb-com/mpb/require-constants-file -- Legacy code: do not use as an example.
export enum LinkListSeparator {
    SLASH = 'slash',
    DOT = 'dot',
}

// eslint-disable-next-line @mpb-com/mpb/require-constants-file -- Legacy code: do not use as an example.
export enum LinkListVariant {
    INLINE = 'inline',
    BLOCK = 'block',
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- Legacy code: do not use as an example.
export interface LinkListProps {
    /**
     * An array of links to be displayed in the link list.
     */
    links: LinkProps[];

    /**
     * The variant of the link list, one of:
     * 1. INLINE
     * 2. BLOCk
     *
     * @default INLINE
     */
    variant?: LinkListVariant;

    /**
     * The character used to separate the links, one of:
     * 1. SLASH
     * 2. DOT
     */
    separator?: LinkListSeparator;

    /**
     * The size of the separator between the links, one of:
     * 1. SMALL
     * 2. MEDIUM
     *
     * @default SMALL
     */
    separatorSize?: LinkSize;

    /**
     * Whether this list should be ordered or not
     * @default false
     */
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    ordered?: boolean;

    /**
     * Denotes whether or not this link list is to be used for navigation
     */
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    navigation?: boolean;

    /**
     * Custom className
     */
    className?: string;

    'data-id'?: string;
}

/**
 * Gets the element to display. Either a ul or ol
 * @param {boolean} isOrdered - Whether the link list should be ordered
 * @return {string} The element type
 */
function getElement(isOrdered: boolean): React.ElementType {
    return isOrdered ? 'ol' : 'ul';
}

// eslint-disable-next-line @mpb-com/mpb/require-types-file -- Legacy code: do not use as an example.
export type StyledLinkListItemProps = LinkListProps & EmotionDynamicElement;

const StyledLinkListItem = styled.div.withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})<StyledLinkListItemProps>`
    ${styles}
`;

export const LinkList: FunctionComponent<LinkListProps> = ({
    links,
    separator,
    separatorSize = LinkSize.SMALL,
    variant = LinkListVariant.INLINE,
    navigation = false,
    ordered = false,
    'data-id': dataId,
    ...otherProps
}: LinkListProps) => {
    /**
     * Get a link list item as a ReactNode
     * @param {LinkProps} link - The link to display
     * @param {number} i - The index of the link in he list
     * @returns {ReactNode} The link list item as a ReactNode
     */
    function getLinkListItem(link: LinkProps, i: number): ReactNode {
        return (
            <li key={i} data-id={dataId}>
                {link.href ? (
                    <WrappedLink
                        {...(link as WrappedLinkProps)}
                        data-id={`${dataId}__link`}
                        linkColor={LinkColor.NAVIGATION}
                        as={link.href}
                        href={link.templatePath || link.href}
                    />
                ) : (
                    <Link
                        linkColor={LinkColor.NAVIGATION}
                        {...link}
                        data-id={`${dataId}__link`}
                    />
                )}
            </li>
        );
    }

    /**
     * Get a list of link items as a ReactNode
     * @param {LinkProps} links - An array of links to display
     * @returns {ReactNode} The list of links as a ReactNode
     */
    function getLinksList(links: LinkProps[]): ReactNode[] {
        return links.map((link: LinkProps, i: number) => {
            return getLinkListItem(link, i);
        });
    }

    const Wrapper = navigation ? 'nav' : React.Fragment;

    return (
        <Wrapper>
            <StyledLinkListItem
                links={links}
                variant={variant}
                separator={separator}
                separatorSize={separatorSize}
                navigation={navigation}
                ordered={ordered}
                {...otherProps}
                as={getElement(ordered)}
            >
                {getLinksList(links)}
            </StyledLinkListItem>
        </Wrapper>
    );
};
